import Vue from 'vue';
import VueApp from './main';
import axios from "axios";
import StoreConfiguration from "./store-configuration";
import Utils from "./utils";
import Snomed from './snomed';
import TokenService from '../../common/token.service';
import { updateLastSearchConcepts } from '../../common/historyUtils.js';

class Store {

    constructor() {
        this.state = {
            openConceptsPath: [],
            search: '', // Texte recherché
            searchLoading: false,
            page: 1, // Page ciblée
            itemsPerPage: 10, // Nombre d'éléments par page
            conceptsList: [], // Liste des concepts
            facetsList: [], // Liste des facettes
            suggestions: [], // Liste des suggestions
            numberOfConcepts: 0, // Nombre total de concepts
            numberOfConceptsWithAlignement: 0, // Nombre de concepts visibles avec alignement
            terminologiesList: [], // Liste de toutes les terminologies
            alignementsList: [], // Liste de tous les alignements
            searchType: 'classic', // Type de recherche (classique ou avancée). Classique par défaut
            advancedSearchQuery: {},
            advancedSearchConfig: undefined,
            terminologyId: '',
            terminologyDetail: {},
            terminologyLanguages: [],
            conceptId: '',
            configurationsFiltresAxiomesChange: false,
            conceptDetail: {},
            individualParentProps: {},
            errorInQuery: false,
            SpecifiqueFacetInputList: {},
            facets: {
                options: {
                    all: [],
                    selected: []
                },
                UUID: {
                    mapping: {},
                    reverseMapping: {}
                }
            },
            alignementFacets: {
                options: {
                    all: []
                },
                UUID: {
                    mapping: {},
                    reverseMapping: {}
                }
            },
            mappingListWithCounts: {}, // Liste des mappings pour le détail d'un concept
            conceptSnackbars: [], // Liste des snackbars pour le détail d'un concept
            selectedTab:undefined,
        };
    }

    /**
     * Récupère l'onglet sélectionné. Si aucun onglet n'est défini, retourne "detail" par défaut.
     * @returns {string} L'onglet sélectionné
     */
    getSelectedTab(){
        if (this.state.selectedTab == undefined){
            this.state.selectedTab = "detail";
        }
        return this.state.selectedTab;
    }

    /**
     * Définit l'onglet sélectionné.
     * @param {string} tab - Le nom de l'onglet à sélectionner
     */
    setSelectedTab(tab){
        this.state.selectedTab = tab;
    }

    /**
     * Définit le texte de recherche.
     * @param {string} search - Le texte à rechercher
     */
    setSearch(search) {
        this.state.search = search;
    }

    /**
     * Définit la page actuelle pour la pagination.
     * @param {number} page - Le numéro de page
     */
    setPage(page) {
        this.state.page = page;
    }

    /**
     * Définit le nombre d'éléments par page.
     * @param {number} itemsPerPage - Le nombre d'éléments à afficher par page
     */
    setItemsPerPage(itemsPerPage) {
        this.state.itemsPerPage = itemsPerPage;
    }

    /**
     * Définit le type de recherche (classique ou avancée).
     * @param {string} searchType - Le type de recherche ('classic' ou 'advanced')
     */
    setSearchType(searchType) {
        this.state.searchType = searchType;
    }

    /**
     * Récupère la liste des facettes spécifiques.
     * @returns {Object} La liste des facettes spécifiques
     */
    getSpecifiqueFacetInputList() {
        return this.state.SpecifiqueFacetInputList;
    }

    /**
     * Définit la liste des facettes spécifiques.
     * @param {Object} specifiqueFacetInputList - La liste des facettes spécifiques
     */
    setSpecifiqueFacetInputList(specifiqueFacetInputList) {
        this.state.SpecifiqueFacetInputList = specifiqueFacetInputList;
    }

    /**
     * Récupère les terminologies sélectionnées depuis le store avec toutes leurs informations.
     * @returns {Array} Tableau des objets terminologies sélectionnées avec label, docCount, protected, etc.
     */
    getSelectedTerminologies() {
        return this.state.facets.options.all.filter(terminology => terminology.checked === true);
    }

    /**
     * Récupère la liste des terminologies depuis l'API avec le nombre de concepts.
     * @returns {Promise} Résultat de l'appel API
     */
    async getTerminologiesNameFromApi() {

        await this.refreshUserTokenIfExpired();

        let endpoint = '/api/terminologies/list/with-concepts';
        let params = {'isBrowserSnomedLicence' : Snomed.checkIfSnomedBrowerLicenceValid()}

        return await axios.get(
            Vue.prototype.$searchResultManager.middlewareBaseUrl + endpoint,
            {params: params, headers: this.buildAuthorizationHeader() }
        ).then((response) => { // Gérer le succès
            this.state.terminologiesList = response.data;
        });
    }

    /**
     * Vérifie si des terminologies sont sélectionnées.
     * @returns {boolean} True si des terminologies sont sélectionnées, sinon false
     */
    hasSelectedTerminologies() {
        let hasSelectedTerminologies = false;
        for (const terminology of this.state.facets.options.all) {
            if (terminology.checked) {
                hasSelectedTerminologies = true;
            }
        }
        return hasSelectedTerminologies;
    }

    /**
     * Vérifie si au moins une valeur de facette spécifique est cochée.
     * @returns {boolean} True si au moins une valeur est cochée, sinon false
     */
    isTerminologySpecificFacetValuesChecked() {
        if (!Array.isArray(this.state.facetsList) || this.state.facetsList.length === 0) {
            return false;
        }

        return this.state.facetsList.some((facet) =>
            Array.isArray(facet?.terminologyFacets) &&
            facet.terminologyFacets.some((terminologyFacet) =>
                Array.isArray(terminologyFacet?.terminologySpecificFacetValues) &&
                terminologyFacet.terminologySpecificFacetValues.some((value) => value?.checked === true)
            )
        );
    }


    /**
        * Vérifie si des terminologies existent dans les facettes.
        * @return {boolean} True si des terminologies existent, sinon false    
    */
    hasTerminologiesInFacets() {
        return this.state.facets.options.all.length > 0;
    }

    /**
     * Remet à zéro les résultats de recherche et les facettes.
     */
    resetSearchResults() {
        this.state.terminologiesList = [];
        this.state.alignementsList = [];
        this.state.advancedSearchQuery = undefined;
        this.state.conceptsList = [];
        this.state.numberOfConcepts = 0;
        this.state.numberOfConceptsWithAlignement = 0;
        this.state.facets.options.all = [];
        this.state.facets.options.selected = [];
        this.state.alignementFacets.options.all = [];
        this.state.suggestions = [];
    }

    /**
     * Effectue une recherche classique de concepts.
     * @param {boolean} isFacetEnable - Indique si les facettes sont activées
     * @returns {Promise} Résultat de la recherche
     */
    async searchConcepts(isFacetEnable,terminologiesFilter, appendResults) {
        // Rechercher uniquement s'il y a au moins 3 caractères
        if (!this.state.search || this.state.search.length < 1) {
            this.resetSearchResults();
            return;
        }
        if (! appendResults){
            this.state.searchLoading = true;
        }


        let terminologySpecifiqueFacetInputList = [];
        if (terminologiesFilter !== undefined && terminologiesFilter !== null){
            for (const facet of this.state.facetsList) {
                
                if (facet.terminologyFacets && Array.isArray(facet.terminologyFacets)) {
                    for (const terminologyFacet of facet.terminologyFacets) {
                        let selectedValues = [];
                        if (terminologyFacet.terminologySpecificFacetValues && Array.isArray(terminologyFacet.terminologySpecificFacetValues)) {
                            for (const value of terminologyFacet.terminologySpecificFacetValues) {
                                if (value.checked) {
                                    selectedValues.push(value.label);
                                }
                            }
                        }
                        if (selectedValues.length > 0) {
                            terminologySpecifiqueFacetInputList.push({
                                id: terminologyFacet.id,
                                values: selectedValues
                            });
                        }
                    }
                }
            }
        }

        try {
            await this.refreshUserTokenIfExpired();
            let endpoint = '/api/concepts/search';
            let params = {
                'searchedText': this.state.search,
                'pagination': this.state.page,
                'size': this.state.itemsPerPage,
                'adv': this.state.searchType === 'advanced' ? true : false,
                'terminologyFilters': Array.isArray(terminologiesFilter) ? terminologiesFilter.join(',') : terminologiesFilter,
                'isBrowserSnomedLicence' : Snomed.checkIfSnomedBrowerLicenceValid()
            };
            if (VueApp.$route.query.lang) {
                params.lang = VueApp.$route.query.lang;
            }
            if(VueApp.$route.query.exact === 'true') {
                params.exact = true;
            }

            let result = await this.getConceptsFromApi(endpoint, params, isFacetEnable,appendResults,terminologySpecifiqueFacetInputList);
            return result;
        } finally {
            this.state.searchLoading = false;
        }
    }

    

    /**
     * Ajoute la propriété checked à tous les terminologyFacets et leurs valeurs
     * @param {Array} facets - Les facettes à compléter
     * @private
     */
    addCheckedPropertyToFacets(facets) {
        if (!facets || !Array.isArray(facets)) {
            return;
        }

        for (const facet of facets) {
            if (facet.terminologyFacets && Array.isArray(facet.terminologyFacets)) {
                for (const terminologyFacet of facet.terminologyFacets) {
                    terminologyFacet.checked = false;
                    if (terminologyFacet.expanded === undefined) {
                        terminologyFacet.expanded = false;
                    }
                    if (terminologyFacet.terminologySpecificFacetValues && Array.isArray(terminologyFacet.terminologySpecificFacetValues)) {
                        for (const value of terminologyFacet.terminologySpecificFacetValues) {
                            value.checked = false;
                        }
                    }
                }
            }
        }
    }

    /**
     * Fusionne intelligemment la liste des facettes avec les nouvelles facettes reçues.
     * Supprime les facettes qui ne sont plus présentes et ajoute les nouvelles.
     * @param {Array} newFacets - Les nouvelles facettes à fusionner
     * @private
     */
    mergeFacetsList(newFacets) {
        if (!newFacets) {
            return;
        }

        if (!this.state.facetsList) {
            this.state.facetsList = newFacets;
            return;
        }

        const newFacetsMap = new Map(newFacets.map(f => [f.terminoName, f]));
        
        // Il y a 2 cas à prévoir ici : soit on a supprimé une facette terminologie, soit on en a ajouté une
        if (this.state.facetsList.length > newFacetsMap.size) {
            // Supprimer les facettes qui ne sont plus dans la réponse
            this.state.facetsList = this.state.facetsList.filter(existingFacet => 
                newFacetsMap.has(existingFacet.terminoName)
            );
        } else {
            // Ajouter les nouvelles facettes
            for (const [terminoName, newFacet] of newFacetsMap.entries()) {
                const existingIndex = this.state.facetsList.findIndex(f => f.terminoName === terminoName);
                if (existingIndex === -1) {
                    this.state.facetsList.push(newFacet);
                }
            }
        }
    }


    /**
     * 
     */
    resetFacetsList() {
        this.state.facetsList = [];
        this.state.alignementsList = [];
        this.state.alignementFacets.options.all = [];
    }

    calculateNumberOfConceptsWithAlignement() {
        let visibleCountWithAlignement = 0;
        
        if (this.state.alignementsList && Array.isArray(this.state.alignementsList)) {
            for (const facet of this.state.alignementsList) {
                // Vérifier si la facette est visible
                if (facet.visible === true) {
                    visibleCountWithAlignement += facet.docCount || 0;
                }
            }
        }
        this.state.numberOfConceptsWithAlignement = visibleCountWithAlignement;
    }


    /**
     * Appelle l'API pour récupérer les concepts avec les facettes sélectionnées.
     * @param {string} endpoint - L'endpoint de l'API
     * @param {Object} params - Les paramètres de la requête
     * @param {boolean} isFacetEnable - Indique si les facettes sont activées
     * @param {boolean} appendResults - Indique si les résultats doivent être ajoutés à la liste existante
     * @param {Array} terminologySpecifiqueFacetInputList - Liste des TerminologySpecifiqueFacetInput
     * @returns {Promise} Résultat de l'appel API
     */
    async getConceptsFromApi(endpoint, params, isFacetEnable, appendResults, terminologySpecifiqueFacetInputList) {
        // genérer terminologySpecifiqueFacetInputList à partir des facettes sélectionnées
        const queryString = Utils.buildCustomParamsForAxios(params);
        this.state.errorInQuery = false;

        const requestBody = {
            facets: [], // Déprécié, mais nécessaire pour l'API
            terminologySpecifiqueFacets: terminologySpecifiqueFacetInputList || [],
            alignements: (this.state.alignementFacets.options.all || [])
                .filter((alignement) => alignement.checked)
                .map((alignement) => ({
                idAlignement: alignement.id,
            })),
        };

        return await axios.post(
            Vue.prototype.$searchResultManager.middlewareBaseUrl + endpoint + '?' + queryString,
            requestBody,
            { headers: this.buildAuthorizationHeader() }
        ).then(async (response) => { // Gérer le succès
            
            if (appendResults){
                // Fusionner les nouvelles terminologies avec la liste existante
                this.state.conceptsList = [
                    ...this.state.conceptsList,
                    ...response.data.concepts
                ];
                
                this.state.numberOfConcepts = response.data.numberOfConcepts;
                
            }else{
                this.state.conceptsList = (response.data.concepts && true) ? [...response.data.concepts] : this.state.conceptsList;
                this.state.numberOfConcepts = response.data.numberOfConcepts;
                this.state.suggestions = response.data.suggest;
            
                // Ajouter checked: true à chaque terminologySpecificFacetValues
                this.addCheckedPropertyToFacets(response.data.facets);
                
                // Fusionner facetsList: supprimer les éléments absents de la réponse, ajouter les nouveaux
                this.mergeFacetsList(response.data.facets);

                this.mergeAlignementFacets(response.data.alignementFacets);

                if (response.data.numberOfConcepts !== undefined) {
                    updateLastSearchConcepts(response.data.numberOfConcepts);
                }

                if (!isFacetEnable && response.data.facets) {
                    await this.initFacetTerminologies(response.data.facets)
                }
            }

            this.calculateNumberOfConceptsWithAlignement();

        }).catch(( ) => {
            this.state.errorInQuery = true;
            this.resetSearchResults();
        });
    }


    /**
     * Charge la page suivante de concepts et les ajoute à la liste existante.
     * @param {string} endpoint - L'endpoint de l'API à appeler
     * @param {Object} params - Les paramètres de la requête
     */
    async loadMoreConcepts(page) {       
        this.setPage(page);
        const terminologiesFilter = this.getSelectedTerminologies().map(terminology => terminology.id);
        return await this.searchConcepts(true, terminologiesFilter, true);
    }


    /**
     * Appelle l'API pour la recherche avancée avec des requêtes booléennes.
     * @param {string} endpoint - L'endpoint de l'API
     * @param {Object} params - Les paramètres de la requête
     * @param {Object} query - La requête booléenne avancée
     * @param {boolean} isFacetEnable - Indique si les facettes sont activées
     * @returns {Promise} Résultat de l'appel API
     */
    async getConceptsFromAdvancedApi(endpoint, params, query, isFacetEnable) {
        let facets =  this.state.facets.options.selected;
        let terminologiesFilter = this.getSelectedTerminologies();
        if(terminologiesFilter) {
            for (const t of terminologiesFilter) {
                let facetExists = facets ? facets.find(f => f.terminoName === t) : null;
                if(!facetExists) {
                    facets.push({ terminoName: t});
                }
            }
        }
        const axiosParams = Utils.buildCustomParamsForAxios(params);
        return await axios.post(
            Vue.prototype.$searchResultManager.middlewareBaseUrl + endpoint + '?' + axiosParams,
            { query, facets },
            { headers: this.buildAuthorizationHeader()  }
        ).then(async(response) => { // Gérer le succès
            this.state.conceptsList = (response.data.concepts && true) ? [...response.data.concepts] : this.state.conceptsList;
            this.state.numberOfConcepts = response.data.numberOfConcepts;

            if (!isFacetEnable && response.data.facets) {
                await this.initFacetTerminologies(response.data.facets)
            }
        });
    }

    /**
     * Génère un UUID unique pour éviter les conflits de contrainte unique dans le composant treeview.
     * @param {string} id - L'identifiant à mapper
     * @returns {string} L'UUID généré
     */
    generateFacetUniqueId (id) {

        let UUID = String(Math.floor(Math.random() * Date.now()));

        if (this.state.facets.UUID.mapping[UUID]) this.generateFacetUniqueId(id);

        this.state.facets.UUID.mapping[UUID] = id;

        !(id in this.state.facets.UUID.reverseMapping)
            ? this.state.facets.UUID.reverseMapping[id] = [UUID]
            : this.state.facets.UUID.reverseMapping[id].push(UUID);

        return UUID;
    }

    /**
     * Initialise les options de facettes en tenant compte des données de facettes.
     * Refactorisé pour réduire la complexité en extrayant des sous-fonctions.
     * @param {Array} facetData - Les données des facettes à initialiser
     */
    async initFacetTerminologies(facetData) {

        await StoreConfiguration.getDefaultConfiguration();

        // Récupérer le paramètre 'terminology' de la requête Vue Router
        let terminologiesParam = VueApp.$route.query.terminology;
        // Convertir en tableau si c'est une simple chaîne
        if (terminologiesParam && !Array.isArray(terminologiesParam)) {
            terminologiesParam = [terminologiesParam];
        }

        this.state.facets.options.all = await Promise.all(facetData.map(async (terminology) => {

            await StoreConfiguration.getConceptConfigurationById(terminology.terminoName);

            let dcTypeTraductions = StoreConfiguration.state.concept && StoreConfiguration.state.concept.default_termino && StoreConfiguration.state.concept.default_termino.concepts_details && StoreConfiguration.state.concept.default_termino.concepts_details.affichage
                ? StoreConfiguration.state.concept.default_termino.concepts_details.affichage.dc_type_traductions
                : null;
            dcTypeTraductions = dcTypeTraductions && Object.keys(dcTypeTraductions).length > 0
                ? dcTypeTraductions
                : (StoreConfiguration.state.default.default_termino && StoreConfiguration.state.default.default_termino.concepts_details && StoreConfiguration.state.default.default_termino.concepts_details.affichage
                    ? StoreConfiguration.state.default.default_termino.concepts_details.affichage.dc_type_traductions
                    : null);

            const isProtected = terminology.visible === false;
            const terminologyObj = {
                "UUID": this.generateFacetUniqueId(terminology.terminoName),
                "id": terminology.terminoName,
                "label": this.getDcTypeTranslation(dcTypeTraductions, terminology.label),
                "docCount": terminology.docCount,
                "docTotalCount": terminology.docTotalCount, 
                "protected": isProtected,
                "checked": isProtected ? false : (terminologiesParam === undefined || terminologiesParam.includes(terminology.terminoName))
            };
            return terminologyObj;
        }));

        // Trier les terminologies par libellé
        this.state.facets.options.all.sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Initialise les facettes d'alignement.
     * Les alignements sont décochés par défaut.
     * @param {Array} facetData - Les données alignementFacets retournées par l'API
     */
    mergeAlignementFacets(facetData) {
        if (!Array.isArray(facetData)) {
            this.state.alignementsList = [];
            this.state.alignementFacets.options.all = [];
            return;
        }

        this.state.alignementsList = facetData;

        // Reconstruire the alignementFacets.options.all array with the new data, mai sil faut garder l'information checked pour les alignements déjà sélectionnés
        const selectedAlignements = new Set(
            this.state.alignementFacets.options.all
                .filter(a => a.checked)
                .map(a => a.id)
        );
        this.state.alignementFacets.options.all = facetData.map((alignement) => ({
            UUID: this.generateFacetUniqueId(`alignement-${alignement.idAlignement}`),
            id: alignement.idAlignement,
            label: alignement.label,
            docCount: alignement.docCount,
            docTotalCount: alignement.docTotalCount,
            protected: alignement.visible === false,
            checked: selectedAlignements.has(alignement.idAlignement),
        }));

        this.state.alignementFacets.options.all.sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Récupère la traduction d'un type DC selon la langue courante.
     * @param {Object} translations - Les traductions disponibles
     * @param {string} dcType - Le type DC à traduire
     * @returns {string} La traduction ou le type original si pas de traduction
     */
    getDcTypeTranslation(translations, dcType) {
        const lang = VueApp.$route.query.lang;
        if (lang && translations && translations[dcType] && translations[dcType].find(x => x.lang === lang)) {
            return translations[dcType].find(x => x.lang === lang).traduction;
        } else {
            return dcType;
        }
    }    

    /**
     * Définit l'identifiant de la terminologie courante.
     * @param {string} terminologyId - L'identifiant de la terminologie
     */
    setTerminologyId (terminologyId) {
        this.state.terminologyId = terminologyId;
    }

    /**
     * Définit l'identifiant du concept courant.
     * @param {string} conceptId - L'identifiant du concept
     */
    setConceptId (conceptId) {
        this.state.conceptId = conceptId;
    }

    /**
     * Récupère les détails d'un concept depuis l'API.
     * Met également à jour la liste des mappings.
     * @param {boolean} snomedLicenceBrowser - Indique si la licence SNOMED browser est acceptée
     */
    async getConceptDetail(snomedLicenceBrowser) {

        await this.refreshUserTokenIfExpired();
        let endpoint = '/api/concepts/detail';
        let params = {
            'terminologyId': this.state.terminologyId,
            'conceptId': this.state.conceptId,
            'isBrowserSnomedLicence' : snomedLicenceBrowser
        };

        await axios.get(
            Vue.prototype.$searchResultManager.middlewareBaseUrl + endpoint,
            {params: params, headers: this.buildAuthorizationHeader() }
        ).then(async (response) => { // Gérer le succès
            this.state.conceptDetail = response.data;
        });

        // Mettre à jour la liste des mappings à chaque récupération du détail d'un concept
        await this.getMappingsList(snomedLicenceBrowser);
    }

    /**
     * Récupère les langues disponibles pour la terminologie du concept en cours.
     */
    async getTerminologyLanguages () {
        let terminologyDetail = await this.getTerminologyDetail();
        this.state.terminologyDetail = terminologyDetail.terminology;
        this.state.terminologyLanguages = this.state.terminologyDetail && this.state.terminologyDetail.languages
            ? this.state.terminologyDetail.languages.map((lang) => Utils.convertLangCode(lang)).filter((lang) => lang)
            : [];
    }

    /**
     * Récupère les détails d'une terminologie depuis l'API.
     * @returns {Promise<Object>} Les détails de la terminologie ou un objet vide en cas d'erreur
     */
    async getTerminologyDetail () {
        return await axios.get(
            Vue.prototype.$searchResultManager.middlewareBaseUrl + '/api/terminologies/detail',
            { params: { 'terminologyId': this.state.terminologyId } }
        ).then(response => {
            return response.data;
        }).catch(() => {
            return {};
        });
    }

    /**
     * Vérifie si l'utilisateur est connecté.
     * @returns {Promise<boolean>} True si l'utilisateur est connecté, false sinon
     */
    async checkIfUserIsConnected(){
        if(Vue.prototype.$searchResultManager.isUserLoggedIn){
            return true;
        }
        return false;
    }

    /**
     * Récupère le token d'accès de l'utilisateur.
     * @returns {Promise<string>} Le token d'accès
     */
    async getAccessToken(){
        return Vue.prototype.$searchResultManager.tokens.accessToken;
    }

    /**
     * Appelle l'API WordPress pour actualiser et récupérer le token d'accès utilisateur.
     */
    async refreshUserTokenIfExpired () {
        await TokenService.refreshUserTokenIfExpired(Vue.prototype.$searchResultManager, axios)
    }

    /**
     * Met à jour les paramètres de l'URL en fusionnant les nouveaux paramètres avec les paramètres existants.
     * @param {Object} objectParams - Les paramètres à ajouter ou mettre à jour dans l'URL
     * @param {boolean} [reloadPage=false] - Si true, recharge la page après la mise à jour de l'URL
     */
    updateUrlParameters (objectParams, reloadPage = false) {
        let query = {
            ...VueApp.$route.query,
            ...objectParams
        };

        VueApp.$router.push( { query } ).catch(()=>{}); // Mettre à jour le paramètre de requête

        if (reloadPage) {
            VueApp.$router.go(0); // Recharger la page
        }
    }

    /**
     * Vérifie la limite de longueur de l'URL et supprime un paramètre si nécessaire.
     * @param {string} paramName - Le nom du paramètre à supprimer si l'URL est trop longue
     */
    checkUrlLimit (paramName) {
        if(window.location.href.length > 64000) {
            this.removeUrlParameters(paramName);
        }
    }

    /**
     * Vérifie si un paramètre existe dans l'URL.
     * @param {string} paramName - Le nom du paramètre à vérifier
     * @returns {boolean} True si le paramètre existe, false sinon
     */
    checkUrlParameterExists (paramName) {
        let query = Object.assign({}, VueApp.$route.query);

        return !!query[paramName];
    }

    /**
     * Récupère la valeur d'un paramètre depuis l'URL.
     * @param {string} paramName - Le nom du paramètre à récupérer
     * @returns {any} La valeur du paramètre
     */
    getUrlParameter (paramName) {
        let query = Object.assign({}, VueApp.$route.query);
        return query[paramName];
    }

    /**
     * Vérifie si une variable est de type string.
     * @param {any} variable - La variable à vérifier
     * @returns {boolean} True si la variable est une chaîne de caractères, false sinon
     */
    isTypeOfString (variable) {
        if (variable && typeof variable === "string")
            return true;

        return false;
    }

    /**
     * Récupère les terminologies communes non accessibles entre les paramètres URL et la liste des terminologies.
     * @param {Array|string} urlParametersTerminologies - Les terminologies depuis l'URL
     * @param {Array} terminologiesList - La liste complète des terminologies
     * @param {string} propertyName - Le nom de la propriété à extraire
     * @returns {Array} Les terminologies non accessibles communes
     */
    getCommonNotAccessibleTerminologiesBetweenUrlParametersTerminologiesAndTerminologiesList (urlParametersTerminologies, terminologiesList, propertyName) {
        if (this.isTypeOfString(urlParametersTerminologies))
            urlParametersTerminologies = [urlParametersTerminologies];

        return urlParametersTerminologies
            .map(qTerminology => {
                const found = terminologiesList.find(terminology => qTerminology === terminology.terminologyId && terminology.userHasAccessToConcepts == false);
                return found ? found[propertyName] : null;
            })
            .filter(terminology => typeof terminology === 'string');
    }

    /**
     * Construit l'en-tête d'autorisation avec le token Bearer.
     * @returns {Object} L'en-tête d'autorisation
     */
    buildAuthorizationHeader() {
        return { 'Authorization': 'Bearer ' + Vue.prototype.$searchResultManager.tokens.accessToken };
    }

    /**
     * Charge la configuration du constructeur de requêtes depuis le middleware.
     * @returns {Promise<Object>} La configuration du constructeur de requêtes avancées
     */
    async loadSearchAdvancedConfiguration () {
        if(!this.state.advancedSearchConfig) {
            this.state.advancedSearchConfig = await axios.get(
                Vue.prototype.$searchResultManager.middlewareBaseUrl + '/api/concepts/search/boolean/config'
            ).then((response) => {
                return response.data;
            }).catch(() => {
                return {};
            });
        }
        return this.state.advancedSearchConfig;
    }

    /**
     * Récupère la liste des mappings pour un concept donné.
     * @param {boolean} snomedLicenceBrowser - Indique si la licence SNOMED browser est acceptée
     */
    async getMappingsList(snomedLicenceBrowser) {
        await this.refreshUserTokenIfExpired();
        let endpoint = '/api/concepts/mappings';
        let params = {
            'conceptId': this.state.conceptId,
            'terminologyId': this.state.terminologyId,
            'code': this.state.conceptDetail.code,
            'isBrowserSnomedLicence' : snomedLicenceBrowser
        };

        await axios.get(
            Vue.prototype.$searchResultManager.middlewareBaseUrl + endpoint,
            {params: params, headers: this.buildAuthorizationHeader() }
        ).then(async (response) => {
            this.state.mappingListWithCounts = response.data;
        });
    }
    
    /**
     * Ajoute une snackbar pour les concepts.
     * @param {Object} data - Les données de la snackbar à ajouter
     */
    setConceptSnackbar(data) {
        this.state.conceptSnackbars.push(data);
    }

}

export default new Store();
